import { useEffect, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { TextAreaProps, TextAreaValue } from "./TextArea"
import { TextArea } from "./TextArea"

export interface TextAreaFieldProps extends Omit<TextAreaProps, "name" | "value"> {
	name: string
}

export const TextAreaField = (props: TextAreaFieldProps) => {
	const { name, required, ...rest } = props

	const { formatMessage } = useIntl()
	const { control, trigger } = useFormContext()

	const rules = useMemo(
		() => ({
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}),
		[required, formatMessage],
	)
	const {
		field: { onChange, ref, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		defaultValue: "",
		name,
		rules,
		shouldUnregister: true,
	})

	// Fields need to retrigger their validation when the field is either dirty or the form is submitted.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	useEffect(() => {
		if (isDirty || isSubmitted) {
			trigger(name)
		}
	}, [rules, name, trigger, isDirty, isSubmitted])

	const handleChange = (newValue: TextAreaValue) => onChange(!newValue ? null : newValue)

	return (
		<TextArea
			{...rest}
			ref={ref}
			required={required}
			name={name}
			value={value}
			errorMessage={error?.message}
			onChange={handleChange}
		/>
	)
}
